/**
 * THE DUEL GUN — fire along your heading, and whoever it reaches is in a duel with you, whatever
 * the two of you weigh.
 *
 * The one ability in the registry that does not do something *to* a player. A rocket breaks a
 * cell, a ray freezes one, a patch slows one, a chain drags one; this one puts the two of you in
 * a room together and lets a mini-game decide, which is the same thing a collision between two
 * evenly matched players has always done — with the "evenly matched" taken out.
 *
 * **WHAT IT IS ACTUALLY FOR: A BEGINNER'S ANSWER TO A LEADER.** Everything else in this game
 * scales with mass. You cannot eat somebody bigger, you cannot outrun them for ever, and a rocket
 * costs them a cluster they will rebuild in a minute. A duel is the one mechanic in the game
 * whose outcome does not read anybody's mass at all, and this is the button that reaches it.
 * `DUEL_GUN_STAKE_MULTIPLIER` is what makes that worth crossing the map for: the pot is three
 * times the SMALLER player, so somebody a tenth of a leader's size who lands one is playing for
 * four times what they are worth.
 *
 * **AND POINTING IT DOWNWARD IS A BAD TRADE, WHICH IS THE GRAPPLE'S RULE ARRIVED AT FROM THE
 * OTHER END.** The chain hauls the lighter end whichever way it was fired, so grappling something
 * bigger reels you into its mouth; here the pot is measured in the smaller player, so a leader who
 * guns a beginner risks three times the beginner's mass to win almost nothing — and `transferStake`
 * clamps the beginner's own payment so that they survive it. There is no code path anywhere that
 * says "the big player loses"; the asymmetry falls out of one multiplication and one clamp.
 *
 * **IT FLIES EXACTLY AS A ROCKET DOES, and every number of the flight is imported rather than
 * restated** — `rocket-pack.ts`'s arrangement, for `rocket-pack.ts`'s reason: two entries that
 * were meant to fly the same way and drifted are two subtly different projectiles nobody wrote
 * down. `powerups.test.ts` asserts the two specs agree field for field apart from the effect.
 *
 * The freeze ray pays for what it does with a shorter reach; this one does not, and the reason is
 * that it is the only offensive ability whose price is paid by the *firer* as well. A landed shot
 * freezes both of you for DUEL_DECIDE_TICKS — ten seconds in the open, unable to move, eat, split,
 * eject or fire — and then risks three times the smaller of your two masses on a mini-game. There
 * is no range at which that is free, so charging for it in distance as well would be charging
 * twice.
 */

import {
  DUEL_DECIDE_TICKS,
  DUEL_GUN_STAKE_MULTIPLIER,
  PROJECTILE_MUZZLE_CLEARANCE,
} from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';
import { ROCKET_LIFETIME_TICKS, ROCKET_RADIUS, ROCKET_RANGE, ROCKET_SPEED } from './rocket.js';

export const POWER_UP_DUEL_GUN = 'duel_gun';

/**
 * Shots one orb is worth. **Three.**
 *
 * `charged` rather than `timed`, and the argument is the freeze ray's: a window is chaos because
 * it is burning whether or not you press the button, and a *ten-second window of duel guns* is one
 * player able to hold several people frozen at once — which is the failure mode this ability is
 * nearest to. A duel is worth the same whenever it is fought, so a clock on it would only ever
 * mean "fire it at nobody in particular before it runs out".
 *
 * **More than one**, unlike the teleport, and the difference is that this one can miss. A teleport
 * cannot: it is spent on the tick it is pressed and does exactly what it says. This has to reach a
 * moving target across ROCKET_RANGE, so a single charge would make one bad shot the whole orb.
 *
 * **THREE RATHER THAN TWO, AND THE HONEST ANSWER IS THAT THE CHARGE COUNT WAS NEVER THE RATE
 * LIMIT.** It was two on the reasoning that this is the strongest thing in the registry against a
 * player who is winning, so the budget should be the tightest one bar the teleport's. That reads
 * the ability against the wrong bound: what actually decides how much duelling one orb buys is
 * DUEL_GUN_FIRE_COOLDOWN_TICKS, which is the projectile's own lifetime so two can never be in the
 * air at once, and — far larger — the duel itself, since a shot that LANDS freezes its firer for
 * DUEL_DECIDE_TICKS plus DUEL_REVEAL_TICKS and `stepPowerUpUse` refuses a duellist outright. A
 * player who hits with all three has spent over thirty seconds frozen in the open collecting them,
 * which is a price nothing else here pays. So the third charge is bought almost entirely out of
 * *misses*, and a miss is the one thing about this ability that is nobody's fault but the shooter's
 * aim — which is exactly what a budget should be forgiving of.
 *
 * **WHERE THAT LEAVES IT IN THE LADDER: level with the mine, not under it.** The budgets are one
 * teleport, three mines, three of these, five patches, five rays, and the shape that is still true
 * is the one worth keeping — an ability you AIM at a person gets a small budget, an area-denial
 * body you leave lying around gets five, and the single escape gets one. `powerups.test.ts` pins
 * that shape rather than the old strict `< MINE_CHARGES`, which this number makes false.
 */
export const DUEL_GUN_CHARGES = 3;

/**
 * The gap between two shots, in ticks — **the projectile's own lifetime**, so a player can never
 * have two duel guns in the air at the same time.
 *
 * Derived rather than chosen, exactly as the freeze ray's cooldown is its own freeze and the
 * grapple's is its own haul. It is also the smaller half of the rate limit and says so: **the real
 * one is the duel.** A shot that lands puts its firer in `RoomSim`'s duel freeze for
 * DUEL_DECIDE_TICKS plus DUEL_REVEAL_TICKS, and `stepPowerUpUse` refuses a duellist outright — so
 * the only thing this number is ever bounding is somebody spraying at a target they keep missing.
 */
export const DUEL_GUN_FIRE_COOLDOWN_TICKS = ROCKET_LIFETIME_TICKS;

/**
 * How long a landed shot freezes both players for, in ticks. Not a field on the spec — the duel
 * owns its own clock — and re-exported here only so this file states every bound it is under, the
 * way every other entry in the registry does.
 */
export const DUEL_GUN_FREEZE_TICKS = DUEL_DECIDE_TICKS;

export const duelGunPowerUp: PowerUp = {
  id: POWER_UP_DUEL_GUN,
  name: 'Duel gun',
  description: `Forces the first player it hits into a duel for ${DUEL_GUN_STAKE_MULTIPLIER}x the smaller player's mass — any size.`,
  glyph: '🎯',
  use: {
    model: 'charged',
    charges: DUEL_GUN_CHARGES,
    cooldownTicks: DUEL_GUN_FIRE_COOLDOWN_TICKS,
  },
  // Every number here is the rocket's own, imported rather than restated: one flight, two things
  // waiting at the end of it. See the note at the top of this file.
  projectile: {
    motion: 'launched',
    effect: 'duel',
    speed: ROCKET_SPEED,
    range: ROCKET_RANGE,
    lifetimeTicks: ROCKET_LIFETIME_TICKS,
    // Live from the tick it leaves, for the rocket's reason: it starts outside its firer's own
    // rim and cannot hit them.
    armingTicks: 0,
    radius: ROCKET_RADIUS,
    standoff: PROJECTILE_MUZZLE_CLEARANCE,
    hitsOwner: false,
  },
};
